import { inject, Injectable } from '@angular/core';
import { Router, NavigationError } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';

/**
 * Chunk Load Error Service
 * Handles detection and recovery from lazy-loaded chunk loading failures
 * Automatically reloads the page when chunk loading errors are detected
 * 
 * This is useful when:
 * - A new deployment happens while users are still on the old version
 * - Network issues prevent chunks from loading
 * - Browser cache issues cause stale chunk references
 */
@Injectable({
    providedIn: 'root'
})
export class ChunkLoadErrorService {
    private destroy$ = new Subject<void>();
    private router = inject(Router);

    /**
     * Initialize chunk load error handling
     * Sets up navigation error listener
     */
    public initialize(): void {
        this.setupNavigationErrorHandler();
    }

    /**
     * Setup navigation error handler to detect chunk loading failures
     */
    private setupNavigationErrorHandler(): void {
        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter(event => event instanceof NavigationError)
            )
            .subscribe((event: NavigationError) => {
                const error = event.error;
                
                // Check if it's a chunk loading error
                if (this.isChunkLoadError(error)) {
                    console.warn('Chunk loading failed during navigation, reloading...', error);
                    this.handleChunkLoadError();
                }
            });
    }

    /**
     * Check if an error is a chunk loading error
     * 
     * @param error - The error to check
     * @returns True if error is related to chunk loading
     */
    private isChunkLoadError(error: any): boolean {
        const errorMessage = error?.message || error?.toString() || '';
        const errorName = error?.name || '';
        
        return (
            errorMessage.includes('Loading chunk') ||
            errorMessage.includes('ChunkLoadError') ||
            errorName === 'ChunkLoadError' ||
            (errorMessage.includes('Failed to fetch') && errorMessage.includes('.js')) ||
            errorMessage.includes('error loading dynamically imported module')
        );
    }

    /**
     * Handle chunk load error by reloading the page
     * This ensures users get the latest version of the code
     */
    private handleChunkLoadError(): void {
        // Reload to get the latest version
        window.location.reload();
    }

    /**
     * Cleanup subscriptions
     */
    public destroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
